import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CodeLaboratory = ({ project, isOpen, onClose }) => {
  const [activeFile, setActiveFile] = useState(0);
  const [copied, setCopied] = useState(false);

  if (!project || !isOpen) return null;

  const codeFiles = [
    {
      name: 'component.jsx',
      language: 'javascript',
      content: project.codeSnippets.component
    },
    {
      name: 'hooks.js',
      language: 'javascript', 
      content: project.codeSnippets.hooks
    },
    {
      name: 'styles.css',
      language: 'css',
      content: project.codeSnippets.styles
    },
    {
      name: 'api.js',
      language: 'javascript',
      content: project.codeSnippets.api
    }
  ];

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(codeFiles[activeFile].content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Backdrop */}
      <motion.div
        className="absolute inset-0 bg-background/95 backdrop-blur-md"
        onClick={onClose}
      />

      {/* Code Laboratory Modal */}
      <motion.div
        className="relative w-full max-w-6xl max-h-[90vh] glass-morphism rounded-2xl cyber-border overflow-hidden"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-brand-green to-brand-cyan rounded-lg flex items-center justify-center cyber-glow">
              <Icon name="Code" size={24} color="#ffffff" />
            </div>
            <div>
              <h2 className="font-exo text-2xl font-bold text-foreground">
                Code Laboratory
              </h2>
              <p className="text-text-secondary">{project.title} - Source Analysis</p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              iconName={copied ? "Check" : "Copy"}
              iconPosition="left"
              onClick={copyToClipboard}
              className={`cyber-border transition-all duration-300 ${
                copied ? 'text-brand-green border-brand-green' : 'hover:cyber-glow'
              }`}
            >
              {copied ? 'Copied!' : 'Copy Code'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              onClick={onClose}
              className="hover:bg-surface/50"
            />
          </div>
        </div>

        {/* File Tabs */}
        <div className="flex space-x-1 p-4 bg-surface/20 border-b border-border overflow-x-auto">
          {codeFiles.map((file, index) => (
            <button
              key={index}
              onClick={() => setActiveFile(index)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-mono text-sm whitespace-nowrap transition-all duration-300 ${
                activeFile === index
                  ? 'bg-brand-cyan/20 text-brand-cyan cyber-border' :'text-text-secondary hover:text-foreground hover:bg-surface/30'
              }`}
            >
              <Icon 
                name={
                  file.language === 'javascript' ? 'FileText' :
                  file.language === 'css' ? 'Palette' : 'File'
                } 
                size={16} 
              />
              <span>{file.name}</span>
            </button>
          ))}
        </div>

        {/* Code Content */}
        <div className="p-6 overflow-auto max-h-[60vh]">
          <div className="bg-background/50 rounded-lg p-4 cyber-border">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="ml-4 font-mono text-sm text-text-secondary">
                  {codeFiles[activeFile].name}
                </span>
              </div>
              <div className="text-xs text-text-tertiary font-mono">
                {codeFiles[activeFile].language.toUpperCase()}
              </div>
            </div>

            <pre className="text-sm font-mono overflow-x-auto">
              <code className="text-brand-cyan">
                {codeFiles[activeFile].content}
              </code>
            </pre>
          </div>

          {/* Code Explanation */}
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="font-exo text-lg font-bold text-foreground mb-3">
                Architecture Patterns
              </h3>
              <ul className="space-y-2">
                {project.architecturePatterns.map((pattern, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <Icon name="ArrowRight" size={16} className="text-brand-cyan mt-0.5" />
                    <span className="text-text-secondary">{pattern}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="font-exo text-lg font-bold text-foreground mb-3">
                Best Practices Applied
              </h3>
              <ul className="space-y-2">
                {project.bestPractices.map((practice, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <Icon name="Check" size={16} className="text-brand-green mt-0.5" />
                    <span className="text-text-secondary">{practice}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Performance Insights */}
          <div className="mt-6 bg-surface/30 rounded-lg p-4 cyber-border">
            <h3 className="font-exo text-lg font-bold text-foreground mb-3 flex items-center space-x-2">
              <Icon name="Zap" size={20} className="text-brand-orange" />
              <span>Performance Insights</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-mono font-bold text-brand-cyan">
                  {project.performanceInsights.bundleSize}
                </div>
                <div className="text-text-secondary text-sm">Bundle Size</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-mono font-bold text-brand-green">
                  {project.performanceInsights.loadTime}
                </div>
                <div className="text-text-secondary text-sm">Load Time</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-mono font-bold text-brand-orange">
                  {project.performanceInsights.lighthouse}
                </div>
                <div className="text-text-secondary text-sm">Lighthouse Score</div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default CodeLaboratory;